import type { AuthUser } from '../context/authContextCore'
import type { UserProfile } from '../types/schema'

export const DEBUG_USER: AuthUser = {
  uid: 'debug-no-sign-in-user',
  email: 'debug@timelining.local',
  displayName: 'Debug User',
  photoURL: null,
  emailVerified: true,
  isAnonymous: false,
  providerId: 'debug',
}

export const DEBUG_PROFILE: UserProfile = {
  uid: DEBUG_USER.uid,
  email: DEBUG_USER.email ?? '',
  role: 'MEMBER',
  firstName: 'Debug',
  lastName: 'User',
  username: 'debuguser',
  phone: null,
  displayName: DEBUG_USER.displayName,
  photoURL: null,
  displayPreferences: { type: 'FIRST_LAST', computedName: 'Debug User' },
  helperMetadata: null,
  metadata: { onboardingCompleted: true },
  credits: 250,
  hasSetFirstGoal: true,
  dailyCratesClaimed: true,
  dailyTasksCompletedCount: 0,
  referralCount: 0,
  referredBy: null,
}

/**
 * Helper-side identity bound to the existing debug test phone in
 * src/config/helperTestPhones.ts. Same uid as DEBUG_USER (the debug bypass
 * only mints one auth user), but presented with the phone number on the
 * helper shell so the experience matches the "joined via phone OTP" flow.
 */
export const DEBUG_HELPER_PHONE_E164 = '+917384933720'
export const DEBUG_HELPER_DISPLAY_NAME = 'Helper (7384933720)'

/** Counterparty for seeded chat + meeting data so the helper sees a real two-sided conversation. */
export const DEBUG_ADMIN_COUNTERPARTY = {
  uid: 'debug-admin-counterparty',
  displayName: 'Workspace Admin',
  email: 'admin@debug.local',
}

// --- Role-switcher fixtures ---
//
// The original noSignIn bypass mints exactly one synthetic identity
// (DEBUG_USER above) which always resolves to ADMIN in DEBUG_TEAM_WORKSPACE.
// The fixtures below add a distinct identity for each remaining role tier so
// the floating DebugRoleSwitcher can hot-swap between them at runtime. All of
// these identities share the same DEBUG_TEAM_WORKSPACE — only their role on
// that workspace differs (see DEBUG_BYPASS_ROLE_BY_IDENTITY in src/debug).

export const DEBUG_EDITOR_USER: AuthUser = {
  uid: 'debug-editor',
  email: 'editor@timelining.local',
  displayName: 'Debug Editor',
  photoURL: null,
  emailVerified: true,
  isAnonymous: false,
  providerId: 'debug',
}

export const DEBUG_EDITOR_PROFILE: UserProfile = {
  uid: DEBUG_EDITOR_USER.uid,
  email: DEBUG_EDITOR_USER.email ?? '',
  role: 'MEMBER',
  firstName: 'Debug',
  lastName: 'Editor',
  username: 'debugeditor',
  phone: null,
  displayName: DEBUG_EDITOR_USER.displayName,
  photoURL: null,
  displayPreferences: { type: 'FIRST_LAST', computedName: 'Debug Editor' },
  helperMetadata: null,
  metadata: { onboardingCompleted: true },
  credits: 250,
  hasSetFirstGoal: true,
  dailyCratesClaimed: true,
  dailyTasksCompletedCount: 0,
  referralCount: 0,
  referredBy: null,
}

export const DEBUG_VIEWER_USER: AuthUser = {
  uid: 'debug-viewer',
  email: 'viewer@timelining.local',
  displayName: 'Debug Viewer',
  photoURL: null,
  emailVerified: true,
  isAnonymous: false,
  providerId: 'debug',
}

export const DEBUG_VIEWER_PROFILE: UserProfile = {
  uid: DEBUG_VIEWER_USER.uid,
  email: DEBUG_VIEWER_USER.email ?? '',
  role: 'MEMBER',
  firstName: 'Debug',
  lastName: 'Viewer',
  username: 'debugviewer',
  phone: null,
  displayName: DEBUG_VIEWER_USER.displayName,
  photoURL: null,
  displayPreferences: { type: 'FIRST_LAST', computedName: 'Debug Viewer' },
  helperMetadata: null,
  metadata: { onboardingCompleted: true },
  credits: 250,
  hasSetFirstGoal: true,
  dailyCratesClaimed: true,
  dailyTasksCompletedCount: 0,
  referralCount: 0,
  referredBy: null,
}

/** First helper identity for the role-switcher. Distinct uid from DEBUG_USER
 *  so canPerform doesn't take the owner short-circuit (DEBUG_TEAM_WORKSPACE
 *  is owned by DEBUG_USER). Without this, the helper identity inherits owner
 *  privileges through the workspace ownerId === currentUser.uid check. */
export const DEBUG_HELPER_USER: AuthUser = {
  uid: 'debug-helper',
  email: 'helper@timelining.local',
  displayName: 'Debug Helper',
  photoURL: null,
  emailVerified: true,
  isAnonymous: false,
  providerId: 'debug',
}

export const DEBUG_HELPER_PROFILE: UserProfile = {
  uid: DEBUG_HELPER_USER.uid,
  email: DEBUG_HELPER_USER.email ?? '',
  role: 'HELPER',
  firstName: 'Debug',
  lastName: 'Helper',
  username: 'debughelper',
  phone: DEBUG_HELPER_PHONE_E164,
  displayName: DEBUG_HELPER_USER.displayName,
  photoURL: null,
  displayPreferences: { type: 'PHONE', computedName: DEBUG_HELPER_PHONE_E164 },
  helperMetadata: { internalUid: DEBUG_HELPER_USER.uid, mobileNumber: DEBUG_HELPER_PHONE_E164 },
  metadata: { onboardingCompleted: true },
  credits: 0,
  hasSetFirstGoal: true,
  dailyCratesClaimed: true,
  dailyTasksCompletedCount: 0,
  referralCount: 0,
  referredBy: null,
}

/** Second helper, used for cross-helper isolation cases (helper A cannot edit
 *  helper B's assigned item). */
export const DEBUG_HELPER_2_USER: AuthUser = {
  uid: 'debug-helper-2',
  email: 'helper2@timelining.local',
  displayName: 'Debug Helper #2',
  photoURL: null,
  emailVerified: true,
  isAnonymous: false,
  providerId: 'debug',
}

export const DEBUG_HELPER_2_PROFILE: UserProfile = {
  uid: DEBUG_HELPER_2_USER.uid,
  email: DEBUG_HELPER_2_USER.email ?? '',
  role: 'HELPER',
  firstName: 'Debug',
  lastName: 'Helper 2',
  username: 'debughelper2',
  phone: DEBUG_HELPER_PHONE_E164,
  displayName: DEBUG_HELPER_2_USER.displayName,
  photoURL: null,
  displayPreferences: { type: 'USERNAME', computedName: 'debughelper2' },
  helperMetadata: { internalUid: DEBUG_HELPER_2_USER.uid, mobileNumber: DEBUG_HELPER_PHONE_E164 },
  metadata: { onboardingCompleted: true },
  credits: 0,
  hasSetFirstGoal: true,
  dailyCratesClaimed: true,
  dailyTasksCompletedCount: 0,
  referralCount: 0,
  referredBy: null,
}
